// Canonical subscription types — single source of truth for plan state.
// Mirrors Firestore `users/{uid}.settings.plan`, `settings.trialsUsed`, `settings.devices`.

export type PlanTier = "basic" | "pro" | "ultimate";

export type BillingStatus =
  | "active"
  | "trialing"
  | "past_due"
  | "cancelled_at_cycle_end"
  | "paused"
  | "halted"
  | "cancelled";

export type CadenceDays = 7 | 15 | 30 | 365;

export interface PlanTrial {
  startedAt: string;      // ISO
  endsAt: string;         // ISO
  forTier: "pro" | "ultimate";
}

export interface PlanBilling {
  provider: "razorpay";
  subscriptionId: string;
  status: BillingStatus;
  cadenceDays: CadenceDays;
  nextChargeAt: string | null;    // ISO — when Razorpay will next attempt
  cancelledAt: string | null;
  willDowngradeAt: string | null; // ISO — for cancel-at-cycle-end
  pastDueSince?: string | null;   // ISO — when status first became past_due
}

export interface PlanOneTime {
  purchasedAt: string;  // ISO
  paymentId: string;    // Razorpay payment_id
  amount: number;       // paise
}

export interface PlanHistoryEntry {
  at: string;           // ISO
  action: string;       // "trial_start" | "trial_convert" | "charge" | "cancel_immediate" | "cancel_at_cycle_end" | "auto_downgrade" | "upgrade" | "manual_admin"
  fromTier: PlanTier | null;
  toTier: PlanTier;
  reason: string;
}

export interface Plan {
  tier: PlanTier;
  expiresAt: string | null;         // ISO — access downgrades to basic at this instant
  trial: PlanTrial | null;
  billing: PlanBilling | null;      // null for basic + one-time ultimate
  oneTime: PlanOneTime | null;      // null for recurring; set for Ultimate yearly
  history: PlanHistoryEntry[];      // append-only; keep last 10
}

export interface TrialsUsed {
  any: boolean;                     // set true once user consumes ANY trial
  consumedFor: "pro" | "ultimate" | null;
  consumedAt: string | null;        // ISO
}

export type DeviceType = "mobile" | "desktop";

export type DeviceRole = "owner" | "staff";

export interface ActiveDevice {
  deviceId: string;        // UUID, persisted on device
  type: DeviceType;
  label: string;           // e.g. "Pixel 8" or "Office Windows"
  registeredAt: string;    // ISO
  lastSeenAt: string;      // ISO
  role?: DeviceRole;
  ownerLabel?: string;
}

export interface DevicesState {
  limit: number;                   // computed from plan.tier: basic/pro = 1, ultimate = 3
  active: ActiveDevice[];
  revoked?: Array<{ deviceId: string; revokedAt: string }>;
}

export interface StaffLocks {
  // Forced always-true:
  businessProfile: boolean;
  billing: boolean;
  accountDelete: boolean;
  // Action locks:
  exports: boolean;
  productPricing: boolean;
  transactionDelete: boolean;
  aiCopilot: boolean;
  googleDrive: boolean;
  googleSheets: boolean;
  store: boolean;
  profileView: boolean;
  productEdit: boolean;
  productDelete: boolean;
  stockUpdate: boolean;
  productAdd: boolean;
  barcodePrint: boolean;
  transactionPrint: boolean;
  whatsappShare: boolean;
  // Page locks:
  settings: boolean;
  dashboard: boolean;
  transactions: boolean;
  products: boolean;
  reports: boolean;
  gstReports: boolean; // GST Filing (GSTR-1 + 3B). Default LOCKED for staff — owner must opt in.
}

export interface DesktopInvite {
  code: string;
  createdAt: string;
  expiresAt: string;
  usedBy: null | { deviceId: string; phone: string; uid: string; at: string };
}

export const DEFAULT_STAFF_LOCKS: StaffLocks = {
  businessProfile: true,
  billing: true,
  accountDelete: true,
  exports: true,
  productPricing: false,
  transactionDelete: false,
  aiCopilot: true,
  googleDrive: true,
  googleSheets: true,
  store: true,
  profileView: true,
  productEdit: false,
  productDelete: false,
  stockUpdate: false,
  productAdd: false,
  barcodePrint: false,
  transactionPrint: false,
  whatsappShare: false,
  settings: true,
  dashboard: false,
  transactions: false,
  products: false,
  reports: true,
  gstReports: true, // Default LOCKED — GST filing is owner-only unless owner unlocks for trusted staff.
};

export const DEVICE_LIMITS: Record<PlanTier, number> = {
  basic: 1,
  pro: 1,
  ultimate: 3,
};

export const ULTIMATE_DESKTOP_LIMIT = 2; // within ultimate's 3 devices, max 2 can be desktop
export const PAST_DUE_GRACE_DAYS = 3;
export const PRO_TRIAL_DAYS = 7;
export const ULTIMATE_TRIAL_DAYS = 30;
export const RENEWAL_REMINDER_DAYS = [30, 10, 0] as const;

export function emptyPlan(): Plan {
  return {
    tier: "basic",
    expiresAt: null,
    trial: null,
    billing: null,
    oneTime: null,
    history: [],
  };
}

export function emptyTrialsUsed(): TrialsUsed {
  return { any: false, consumedFor: null, consumedAt: null };
}

export function emptyDevicesState(): DevicesState {
  return { limit: DEVICE_LIMITS.basic, active: [] };
}
